import React from 'react';
import '@/styles/landing-effects.css';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  MessageSquare, Zap, BarChart3, ArrowRight, Check, Menu, X, Mail, Phone, 
  Users, Send, CreditCard, FileSpreadsheet, Clock, Target, ShoppingCart,
  Inbox, PieChart, BellRing, Calendar, Bot, Plug, Shield, Globe,
  UserPlus, RefreshCw, Moon, Sun
} from 'lucide-react';
import { motion } from 'framer-motion';
import ThemeLanguageToggle from '@/components/ThemeLanguageToggle';
import SEOHead from '@/components/SEOHead';
import PricingPlans from '@/components/PricingPlans';
import { FaWhatsapp, FaInstagram, FaFacebookF } from "react-icons/fa";
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  AnimatedCounter,
  LogoMarquee,
  FeatureTabs,
  ChatPreview,
  StickyCTA,
  SectionIntro,
  SectionEyebrow,
  FeatureChecklist,
  ShowcaseFrame,
  SectionGlow,
  DemoScenarios,
  TrustSection,
  EnterpriseBand
} from '@/components/landing';

const HOME_FAQ = [
  {
    q: 'Apa itu Nerava?',
    a: 'Nerava adalah CRM omnichannel dengan AI customer assistant untuk toko online dan UMKM Indonesia. Chat dari WhatsApp, Instagram, Facebook Messenger, dan website masuk ke satu inbox, lalu AI menjawab memakai katalog produk, stok, dan SOP Anda sendiri selama 24 jam.',
  },
  {
    q: 'Berapa harga Nerava?',
    a: 'Trial gratis 30 hari dengan akses penuh. Setelah itu paket Starter Rp 199.000 per bulan, Growth Rp 449.000 per bulan, dan Business Rp 999.000 per bulan. Rincian lengkapnya ada di halaman harga.',
  },
  {
    q: 'Apakah Nerava bisa auto reply WhatsApp?',
    a: 'Bisa. Chat yang masuk ke nomor WhatsApp bisnis Anda dijawab otomatis oleh AI lewat WhatsApp Business API resmi Meta, termasuk mengirim foto produk, menghitung ongkir, dan membaca bukti transfer.',
  },
  {
    q: 'Apakah perlu tim IT untuk memakai Nerava?',
    a: 'Tidak perlu. Semua pengaturan dikerjakan sendiri dari dashboard: hubungkan channel, unggah katalog produk, isi FAQ dan SOP, lalu uji di simulator. Rata-rata setup sekitar 15 menit.',
  },
];

const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Nerava',
    description: 'CRM omnichannel dengan AI customer assistant untuk toko online dan UMKM Indonesia. Auto reply WhatsApp, Instagram, dan Facebook Messenger.',
    url: 'https://nerava.id',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '199000',
      priceCurrency: 'IDR',
      description: 'Paket Starter per bulan. Trial gratis 30 hari dengan akses penuh.',
      url: 'https://nerava.id/harga',
    },
    featureList: [
      'AI Auto Reply WhatsApp',
      'AI Auto Reply Instagram DM',
      'AI Auto Reply Facebook Messenger',
      'CRM & Pipeline Management',
      'Order Management',
      'Multi-channel Inbox',
      'AI Chatbot Builder',
      'Shipping Integration',
      'Invoice Generator',
    ],
    publisher: { '@id': 'https://nerava.id/#organization' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOME_FAQ.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

const LandingPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [activeShowcase, setActiveShowcase] = React.useState(0);

  const handlePricingClick = (planId, billingPeriod = 'monthly') => {
    if (planId === 'enterprise') {
      document.getElementById('enterprise')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (user) {
      navigate(`/payment?plan=${planId}&billing=${billingPeriod}`);
    } else {
      navigate('/register');
    }
  };

  // Main Features for UMKM
  const mainFeatures = [
    {
      icon: <Inbox className="w-6 h-6" />,
      title: t('features.omnichannel'),
      description: t('features.omnichannelDesc'),
      color: "blue"
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: t('features.aiAgent'),
      description: t('features.aiAgentDesc'),
      color: "blue"
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: t('features.orders'),
      description: t('features.ordersDesc'),
      color: "purple"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('features.customers'),
      description: t('features.customersDesc'),
      color: "pink"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: t('features.payment'),
      description: t('features.paymentDesc'),
      color: "amber"
    },
    {
      icon: <FileSpreadsheet className="w-6 h-6" />,
      title: t('features.sheets'),
      description: t('features.sheetsDesc'),
      color: "green"
    }
  ];

  // How It Works - Updated for business flow
  const howItWorks = [
    {
      step: "1",
      icon: <Plug className="w-5 h-5" />,
      title: t('howItWorks.step1Title'),
      description: t('howItWorks.step1Desc'),
      meta: t('howItWorks.step1Meta')
    },
    {
      step: "2",
      icon: <Bot className="w-5 h-5" />,
      title: t('howItWorks.step2Title'),
      description: t('howItWorks.step2Desc'),
      meta: t('howItWorks.step2Meta')
    },
    {
      step: "3",
      icon: <Users className="w-5 h-5" />,
      title: t('howItWorks.step3Title'),
      description: t('howItWorks.step3Desc'),
      meta: t('howItWorks.step3Meta')
    },
    {
      step: "4",
      icon: <ShoppingCart className="w-5 h-5" />,
      title: t('howItWorks.step4Title'),
      description: t('howItWorks.step4Desc'),
      meta: t('howItWorks.step4Meta')
    }
  ];

  // FAQ Items - Updated
  const faqItems = [
    {
      question: t('faq.q1'),
      answer: t('faq.a1')
    },
    {
      question: t('faq.q2'),
      answer: t('faq.a2')
    },
    {
      question: t('faq.q3'),
      answer: t('faq.a3')
    },
    {
      question: t('faq.q4'),
      answer: t('faq.a4')
    },
    {
      question: t('faq.q5'),
      answer: t('faq.a5')
    },
    {
      question: t('faq.q6'),
      answer: t('faq.a6')
    }
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden ${theme === 'dark' ? 'dark' : ''}`}>
      <SEOHead
        title="Nerava: AI Customer Service & CRM Omnichannel untuk Toko Online"
        description="AI customer assistant Nerava membalas chat WhatsApp, Instagram & Messenger 24 jam: jawab harga dan stok, kirim foto produk, hitung ongkir, sampai pesanan masuk. Coba gratis 30 hari, tanpa kartu kredit."
        canonical="https://nerava.id/"
        jsonLd={HOME_JSON_LD}
      />
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors overflow-x-hidden">
        
        {/* Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-[#0A0A0A]/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <img
                  src="/images/logo.png"
                  alt="Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
              </Link>
              
              <div className="hidden lg:flex items-center gap-6">
                <Link to="/ai-customer-service" data-testid="nav-acs-link" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">AI Customer Service</Link>
                <Link to="/ai-whatsapp" data-testid="nav-wa-link" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">AI WhatsApp</Link>
                <Link to="/crm-pesanan" data-testid="nav-crm-link" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">CRM & Pesanan</Link>
                <a href="#demo" data-testid="nav-demo-link" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('eyebrows.demo')}</a>
                <Link to="/harga" data-testid="nav-pricing-link" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav.pricing')}</Link>
                <a href="#faq" className="text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav.faq')}</a>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="hidden sm:block">
                  {/* <ThemeLanguageToggle /> */}
                </div>
                {user ? (
                  <Link to="/dashboard">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-4 sm:px-6 text-xs sm:text-sm">
                      {t('nav.dashboard')}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" className="hidden sm:block">
                      <Button variant="ghost" className="text-slate-600 dark:text-zinc-300">
                        {t('nav.login')}
                      </Button>
                    </Link>
                    <Link to="/register">
                      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap">
                        <span className="hidden sm:inline">{t('hero.cta')}</span>
                        <span className="sm:hidden">Mulai</span>
                      </Button>
                    </Link>
                  </>
                )}
                <button
                  onClick={toggleTheme}
                  data-testid="landing-theme-toggle"
                  aria-label={theme === 'dark' ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'}
                  title={theme === 'dark' ? 'Mode terang' : 'Mode gelap'}
                  className="flex-shrink-0 grid place-items-center w-9 h-9 rounded-full border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500/60 hover:rotate-12 transition-[color,border-color,transform] duration-300"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden p-2 flex-shrink-0">
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="lg:hidden -mx-4 mt-4 px-4 pb-4 border-t border-slate-200 dark:border-zinc-800 pt-4 bg-white dark:bg-[#0A0A0A]">
                <nav className="flex flex-col gap-3">
                  <Link to="/ai-customer-service" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>AI Customer Service</Link>
                  <Link to="/ai-whatsapp" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>AI WhatsApp</Link>
                  <Link to="/crm-pesanan" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>CRM &amp; Pesanan</Link>
                  <a href="#demo" data-testid="nav-demo-link-mobile" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>{t('eyebrows.demo')}</a>
                  <Link to="/harga" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>{t('nav.pricing')}</Link>
                  <a href="#faq" className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMobileMenuOpen(false)}>{t('nav.faq')}</a>
                  <div className="pt-2">
                    {/* <ThemeLanguageToggle /> */}
                  </div>
                </nav>
              </div>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 bg-white dark:bg-[#0A0A0A] bg-pattern-circuit overflow-hidden">
          <SectionGlow position="left" />
          <SectionGlow position="right" />
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div 
                className="text-center lg:text-left"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div 
                  className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300 mb-6"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span>{t('hero.badge')}</span>
                </motion.div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.08]" style={{ fontFamily: 'Manrope' }}>
                  {t('hero.title')}{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500 dark:from-indigo-400 dark:to-blue-400">
                    {t('hero.titleHighlight')}
                  </span>
                </h1>
                
                <motion.p 
                  className="text-lg sm:text-xl text-slate-600 dark:text-zinc-300 mb-8 max-w-xl mx-auto lg:mx-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {t('hero.subtitle')}
                </motion.p>

                <motion.div
                  data-testid="hero-channels"
                  className="mb-7 flex flex-wrap items-center gap-2 justify-center lg:justify-start"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                >
                  {[
                    { label: 'WhatsApp', icon: <FaWhatsapp className="w-3.5 h-3.5" />, tone: 'text-emerald-600 dark:text-emerald-400' },
                    { label: 'Instagram', icon: <FaInstagram className="w-3.5 h-3.5" />, tone: 'text-pink-600 dark:text-pink-400' },
                    { label: 'Messenger', icon: <FaFacebookF className="w-3.5 h-3.5" />, tone: 'text-blue-600 dark:text-blue-400' },
                    { label: 'Website', icon: <Globe className="w-3.5 h-3.5" />, tone: 'text-slate-500 dark:text-zinc-400' },
                  ].map((channel) => (
                    <span
                      key={channel.label}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300"
                    >
                      <span className={channel.tone}>{channel.icon}</span>
                      {channel.label}
                    </span>
                  ))}
                </motion.div>

                <motion.p
                  data-testid="hero-for-who"
                  className="mb-8 text-sm text-slate-500 dark:text-zinc-400 max-w-xl mx-auto lg:mx-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.38 }}
                >
                  {t('hero.forWho')}
                </motion.p>

                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Link to="/register">
                    <Button data-testid="hero-cta-trial" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 py-6 text-base sm:text-lg group shadow-[0_18px_36px_-18px_rgba(79,70,229,0.7)] transition-[background-color,box-shadow,transform] hover:-translate-y-0.5">
                      {t('hero.ctaTrial')}
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <a href="#demo">
                    <Button data-testid="hero-cta-demo" variant="outline" className="w-full sm:w-auto rounded-full px-8 py-6 text-base sm:text-lg border border-slate-300 hover:border-indigo-400 hover:text-indigo-700 dark:border-zinc-700 dark:hover:border-indigo-500/70 dark:hover:text-indigo-300 bg-transparent transition-colors">
                      {t('hero.ctaDemo')}
                    </Button>
                  </a>
                </motion.div>

                <motion.div 
                  className="flex flex-wrap items-center gap-6 justify-center lg:justify-start text-sm text-slate-500"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    {t('hero.allFeatures')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    {t('hero.noCreditCard')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    {t('hero.quickSetup')}
                  </div>
                </motion.div>
              </motion.div>

              {/* Right Content - Illustration */}
              <motion.div 
                className="relative max-w-md mx-auto lg:max-w-none"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div 
                  className="relative bg-white dark:bg-zinc-900 rounded-2xl p-6 border border-slate-200 dark:border-zinc-800 shadow-[0_30px_70px_-40px_rgba(15,23,42,0.5)] dark:shadow-[0_30px_70px_-34px_rgba(0,0,0,0.95)]"
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Dashboard Header with Live Indicator */}
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100 dark:border-zinc-800">
                    <motion.img 
                      src="/images/logo.png" 
                      alt="Nerava" 
                      className="w-10 h-10"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 dark:text-white">Nerava Dashboard</p>
                      <div className="flex items-center gap-1.5">
                        <motion.span 
                          className="w-2 h-2 bg-green-500 rounded-full"
                          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        />
                        <p className="text-xs text-slate-500">Contoh tampilan • 3 {t('hero.channelsConnected')}</p>
                      </div>
                    </div>
                    <motion.div 
                      className="relative"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                    >
                      <BellRing className="w-5 h-5 text-slate-400" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center">3</span>
                    </motion.div>
                  </div>
                  
                  {/* Channel Stats with Animated Numbers */}
                  <div className="flex gap-3 mb-4">
                    {[
                      { icon: FaWhatsapp, label: 'WhatsApp', count: 24, tone: 'bg-emerald-50 dark:bg-emerald-500/10', iconTone: 'text-emerald-600 dark:text-emerald-400', delay: 0.1 },
                      { icon: FaInstagram, label: 'Instagram', count: 12, tone: 'bg-pink-50 dark:bg-pink-500/10', iconTone: 'text-pink-600 dark:text-pink-400', delay: 0.2 },
                      { icon: FaFacebookF, label: 'Facebook', count: 8, tone: 'bg-blue-50 dark:bg-blue-500/10', iconTone: 'text-blue-600 dark:text-blue-400', delay: 0.3 }
                    ].map((channel, idx) => (
                      <motion.div 
                        key={idx}
                        className={`flex-1 ${channel.tone} rounded-xl p-3 text-center cursor-pointer border border-transparent hover:border-indigo-200 dark:hover:border-indigo-500/30 transition-colors`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + channel.delay }}
                        whileHover={{ y: -3 }}
                      >
                        <channel.icon className={`w-6 h-6 ${channel.iconTone} mx-auto mb-1`} />
                        <p className="text-xs text-slate-600 dark:text-zinc-400">{channel.label}</p>
                        <motion.p 
                          className="text-lg font-bold text-slate-900 dark:text-white"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.8 + channel.delay }}
                        >
                          <AnimatedCounter value={channel.count} duration={1.5} />
                        </motion.p>
                      </motion.div>
                    ))}
                  </div>

                  {/* Live Conversation Feed with Stagger Animation */}
                  <div className="space-y-2">
                    {[
                      { avatar: 'WA', avatarBg: 'bg-emerald-500', name: 'Ahmad - Toko Sejahtera', msg: 'Mau pesan 50 pcs kaos...', badge: 'Hot Lead', badgeColor: 'bg-indigo-600 text-white', highlight: true, delay: 0.9 },
                      { avatar: 'IG', avatarBg: 'bg-pink-500', name: '@fashionista_jkt', msg: 'Harga grosir berapa ya?', time: '2m ago', delay: 1.1 },
                      { avatar: 'FB', avatarBg: 'bg-blue-500', name: 'Budi Santoso', msg: 'Pembayaran diterima', badge: 'Paid', badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300', delay: 1.3 }
                    ].map((conv, idx) => (
                      <motion.div 
                        key={idx}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${conv.highlight ? 'bg-indigo-50 dark:bg-indigo-500/10 border-l-2 border-indigo-500' : 'bg-slate-50 dark:bg-zinc-800/50 hover:bg-slate-100 dark:hover:bg-zinc-800'}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: conv.delay }}
                        whileHover={{ x: 5 }}
                      >
                        <motion.div 
                          className={`w-8 h-8 rounded-full ${conv.avatarBg} flex items-center justify-center text-white text-xs`}
                          whileHover={{ scale: 1.1 }}
                        >
                          {conv.avatar}
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{conv.name}</p>
                          <p className="text-xs text-slate-500 truncate">{conv.msg}</p>
                        </div>
                        {conv.badge && (
                          <motion.span 
                            className={`text-xs ${conv.badgeColor} px-2 py-1 rounded-full`}
                            animate={conv.highlight ? { scale: [1, 1.05, 1] } : {}}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            {conv.badge}
                          </motion.span>
                        )}
                        {conv.time && <span className="text-xs text-slate-500">{conv.time}</span>}
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Typing Indicator */}
                  <motion.div 
                    className="mt-3 flex items-center gap-2 text-xs text-slate-400"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                  >
                    <Bot className="w-4 h-4 text-indigo-500" />
                    <span>AI Agent sedang membalas 2 chat...</span>
                    <motion.div className="flex gap-0.5">
                      {[0, 1, 2].map(i => (
                        <motion.span 
                          key={i}
                          className="w-1.5 h-1.5 bg-indigo-500 rounded-full"
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.15 }}
                        />
                      ))}
                    </motion.div>
                  </motion.div>
                </motion.div>
                
                {/* Decorative Elements with Animation */}
                <motion.div 
                  className="absolute -bottom-6 -right-6 w-32 h-32 bg-indigo-400 rounded-full blur-3xl opacity-25 dark:opacity-30"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.35, 0.2] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <motion.div 
                  className="absolute -top-6 -left-6 w-32 h-32 bg-blue-400 rounded-full blur-3xl opacity-25 dark:opacity-30"
                  animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.18, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Partnership Section */}
        <section className="py-16 sm:py-20 px-4 bg-white dark:bg-zinc-900 border-y border-slate-200 dark:border-zinc-800">
          <LogoMarquee />
        </section>

        {/* Main Features */}
        <section id="features" className="relative py-20 sm:py-24 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden">
          <SectionGlow position="right" />
          <div className="relative max-w-7xl mx-auto">
            <SectionIntro
              testId="features-heading"
              eyebrow={t('eyebrows.features')}
              title={t('features.title')}
              subtitle={t('features.subtitle')}
              note={t('featuresNote')}
              className="mb-14 sm:mb-16"
            />

            <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" staggerDelay={0.08}>
              {mainFeatures.map((feature, index) => (
                <StaggerItem key={index} className="group flex">
                  <motion.div
                    data-testid={`feature-card-${index + 1}`}
                    className="relative flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-indigo-400 hover:shadow-[0_24px_48px_-24px_rgba(79,70,229,0.45)] dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-indigo-500/70"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-5 right-1 select-none text-[84px] font-bold leading-none text-slate-900/[0.04] dark:text-white/[0.05]"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    <div className="relative w-11 h-11 mb-5 rounded-xl flex items-center justify-center bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 transition-colors duration-300">
                      {feature.icon}
                    </div>

                    <h3 className="relative text-lg font-semibold text-slate-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="relative text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                      {feature.description}
                    </p>

                    <span
                      aria-hidden="true"
                      className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-indigo-500 to-blue-500 transition-all duration-300 group-hover:w-full"
                    />
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Demo Produk — simulasi alur nyata, bukan fitur karangan */}
        <section
          id="demo"
          data-testid="demo-section"
          className="relative py-20 sm:py-24 px-4 bg-white dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800 overflow-hidden"
        >
          <SectionGlow position="left" />
          <div className="relative max-w-7xl mx-auto">
            <SectionIntro
              testId="demo-heading"
              eyebrow={t('eyebrows.demo')}
              title={t('demoSection.title')}
              subtitle={t('demoSection.subtitle')}
              className="mb-12 sm:mb-14"
            />
            <DemoScenarios />
          </div>
        </section>

        {/* How It Works — timeline berjalan, bukan grid kartu identik */}
        <section
          id="how-it-works"
          data-testid="how-it-works-section"
          className="relative py-20 sm:py-24 px-4 bg-white dark:bg-zinc-900 bg-pattern-circuit overflow-hidden"
        >
          {/* aksen cahaya lembut di kiri atas, memberi kedalaman tanpa gradient norak */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl dark:bg-indigo-500/20"
          />

          <div className="relative max-w-7xl mx-auto">
            {/* Header asimetris: judul di kiri, catatan di kanan */}
            <AnimatedSection className="mb-14 sm:mb-20 grid gap-6 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2.5 mb-5">
                  <span className="h-px w-8 bg-indigo-500" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                    {t('howItWorks.eyebrow')}
                  </span>
                </div>
                <h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {t('howItWorks.title')}
                </h2>
                <p className="mt-4 text-base md:text-lg text-slate-600 dark:text-zinc-300 max-w-xl">
                  {t('howItWorks.subtitle')}
                </p>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <p
                  data-testid="how-it-works-footnote"
                  className="text-sm text-slate-500 dark:text-zinc-400 lg:ml-auto max-w-sm border-l-2 lg:border-l-0 lg:border-r-2 border-indigo-500/40 pl-4 lg:pl-0 lg:pr-4"
                >
                  {t('howItWorks.footnote')}
                </p>
              </div>
            </AnimatedSection>

            <StaggerContainer className="grid gap-y-2 lg:grid-cols-4 lg:gap-x-6" staggerDelay={0.12}>
              {howItWorks.map((item, index) => (
                <StaggerItem key={index} className="group relative pl-16 pb-10 lg:pl-0 lg:pb-0 lg:flex lg:flex-col">
                  {/* Rel penghubung: vertikal di mobile, horizontal di desktop */}
                  {index < howItWorks.length - 1 && (
                    <>
                      <span
                        aria-hidden="true"
                        className="lg:hidden absolute left-6 top-14 -bottom-1 w-px bg-gradient-to-b from-indigo-500/50 to-indigo-500/5"
                      />
                      <span
                        aria-hidden="true"
                        className="hidden lg:block absolute top-6 left-14 -right-6 h-px bg-gradient-to-r from-indigo-500/50 to-indigo-500/10"
                      />
                    </>
                  )}

                  {/* Node bernomor: duduk di atas rel */}
                  <div className="absolute left-0 top-0 lg:static lg:mb-7">
                    <div className="relative w-12 h-12">
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 rounded-full bg-indigo-500/25 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      />
                      <div className="relative w-12 h-12 rounded-full flex items-center justify-center text-base font-bold ring-4 ring-white dark:ring-zinc-900 border border-indigo-200 bg-white text-indigo-600 dark:border-indigo-500/40 dark:bg-zinc-950 dark:text-indigo-300 group-hover:border-indigo-500 group-hover:text-indigo-700 dark:group-hover:text-white transition-colors duration-300">
                        {item.step}
                      </div>
                    </div>
                  </div>

                  {/* Kartu langkah */}
                  <motion.div
                    data-testid={`how-step-${item.step}`}
                    className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 lg:flex-1 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-indigo-400 hover:shadow-[0_24px_48px_-24px_rgba(79,70,229,0.45)] dark:border-zinc-800 dark:bg-[#0A0A0A] dark:hover:border-indigo-500/70"
                    whileHover={{ transition: { duration: 0.2 } }}
                  >
                    {/* angka besar sebagai tekstur editorial */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-5 right-1 select-none text-[84px] font-bold leading-none text-slate-900/[0.04] dark:text-white/[0.05]"
                    >
                      {item.step}
                    </span>

                    <div className="relative flex items-center justify-between gap-3 mb-5">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 transition-colors duration-300">
                        {item.icon}
                      </div>
                      <span
                        data-testid={`how-step-meta-${item.step}`}
                        className="shrink-0 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500 dark:border-zinc-700 dark:text-zinc-400"
                      >
                        {item.meta}
                      </span>
                    </div>

                    <h3
                      data-testid={`how-step-title-${item.step}`}
                      className="relative text-lg font-semibold text-slate-900 dark:text-white mb-2"
                    >
                      {item.title}
                    </h3>
                    <p className="relative text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                      {item.description}
                    </p>

                    {/* garis aksen yang tumbuh saat hover */}
                    <span
                      aria-hidden="true"
                      className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-indigo-500 to-blue-500 transition-all duration-300 group-hover:w-full"
                    />
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Showcase: Omnichannel with Interactive Chat Preview */}
        <section className="relative py-16 sm:py-24 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden">
          <SectionGlow position="left" />
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <AnimatedSection direction="left">
                <SectionEyebrow className="mb-5">{t('features.omnichannel')}</SectionEyebrow>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  {t('showcase.inbox.title')}
                </h2>
                <p className="text-base md:text-lg text-slate-600 dark:text-zinc-300 mb-7 max-w-lg">
                  {t('showcase.inbox.subtitle')}
                </p>
                <FeatureChecklist
                  testId="showcase-inbox-list"
                  direction="left"
                  items={[
                    t('showcase.inbox.feature1'),
                    t('showcase.inbox.feature2'),
                    t('showcase.inbox.feature3'),
                    t('showcase.inbox.feature4'),
                    t('showcase.inbox.feature5')
                  ]}
                />
                <p
                  data-testid="showcase-sample-data-note"
                  className="mt-6 border-l-2 border-slate-300 pl-4 text-sm text-slate-500 dark:border-zinc-700 dark:text-zinc-400"
                >
                  Tampilan antarmuka di halaman ini memakai data contoh.
                </p>
              </AnimatedSection>
              <AnimatedSection direction="right" className="w-full">
                <ChatPreview />
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Showcase: Orders Management */}
        <section className="relative py-16 sm:py-24 px-4 bg-white dark:bg-zinc-900 overflow-hidden">
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <AnimatedSection direction="left" className="order-2 lg:order-1 w-full max-w-sm sm:max-w-md mx-auto lg:max-w-none">
                <ShowcaseFrame testId="showcase-orders-mockup">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[
                      { label: 'Total', value: '156', tone: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300' },
                      { label: 'Pending', value: '12', tone: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300' },
                      { label: 'Proses', value: '8', tone: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300' },
                      { label: 'Selesai', value: '136', tone: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300' }
                    ].map((stat, idx) => (
                      <motion.div 
                        key={idx} 
                        className={`${stat.tone} rounded-lg p-2 text-center`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="text-lg font-bold">{stat.value}</div>
                        <div className="text-xs text-slate-500 dark:text-zinc-400">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                  {/* Filter Bar */}
                  <div className="flex gap-2 mb-3">
                    <div className="flex-1 bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 text-xs text-slate-400 border border-slate-200 dark:border-zinc-800">
                      Cari pesanan...
                    </div>
                    <div className="bg-white dark:bg-zinc-900 rounded-lg px-3 py-2 text-xs border border-slate-200 dark:border-zinc-800">
                      Semua Channel ▼
                    </div>
                  </div>
                  {/* Orders Table */}
                  <div className="bg-white dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-800 overflow-hidden">
                    <div className="grid grid-cols-4 gap-2 p-2 bg-slate-100 dark:bg-zinc-800 text-xs font-medium text-slate-600 dark:text-zinc-300">
                      <div>Order</div>
                      <div>Customer</div>
                      <div>Total</div>
                      <div>Status</div>
                    </div>
                    {[
                      { id: '#1234', customer: 'Budi S.', total: 'Rp 250K', status: 'Paid', tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
                      { id: '#1235', customer: 'Ani R.', total: 'Rp 180K', status: 'Pending', tone: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
                      { id: '#1236', customer: 'Citra D.', total: 'Rp 520K', status: 'Shipped', tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' }
                    ].map((order, idx) => (
                      <motion.div 
                        key={idx} 
                        className="grid grid-cols-4 gap-2 p-2 text-xs border-t border-slate-100 dark:border-zinc-800"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + idx * 0.1 }}
                      >
                        <div className="font-medium text-slate-900 dark:text-white">{order.id}</div>
                        <div className="text-slate-600 dark:text-zinc-300">{order.customer}</div>
                        <div className="text-slate-900 dark:text-white">{order.total}</div>
                        <div><span className={`px-2 py-0.5 rounded-full text-xs ${order.tone}`}>{order.status}</span></div>
                      </motion.div>
                    ))}
                  </div>
                </ShowcaseFrame>
              </AnimatedSection>
              <AnimatedSection direction="right" className="order-1 lg:order-2">
                <SectionEyebrow className="mb-5">{t('features.orders')}</SectionEyebrow>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  {t('showcase.orders.title')}
                </h2>
                <p className="text-base md:text-lg text-slate-600 dark:text-zinc-300 mb-7 max-w-lg">
                  {t('showcase.orders.subtitle')}
                </p>
                <FeatureChecklist
                  testId="showcase-orders-list"
                  direction="right"
                  items={[
                    t('showcase.orders.feature1'),
                    t('showcase.orders.feature2'),
                    t('showcase.orders.feature3'),
                    t('showcase.orders.feature4')
                  ]}
                />
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Showcase: Customer Database */}
        <section className="relative py-16 sm:py-24 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden">
          <SectionGlow position="right" />
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <AnimatedSection direction="left">
                <SectionEyebrow className="mb-5">{t('features.customers')}</SectionEyebrow>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  {t('showcase.customers.title')}
                </h2>
                <p className="text-base md:text-lg text-slate-600 dark:text-zinc-300 mb-7 max-w-lg">
                  {t('showcase.customers.subtitle')}
                </p>
                <FeatureChecklist
                  testId="showcase-customers-list"
                  direction="left"
                  items={[
                    t('showcase.customers.feature1'),
                    t('showcase.customers.feature2'),
                    t('showcase.customers.feature3'),
                    t('showcase.customers.feature4')
                  ]}
                />
              </AnimatedSection>
              <AnimatedSection direction="right" className="w-full max-w-md mx-auto lg:max-w-none">
                <ShowcaseFrame testId="showcase-customers-mockup">
                  {/* Stats Row */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[
                      { label: 'Total', value: '847', icon: <Users className="w-5 h-5 text-indigo-500 dark:text-indigo-400" /> },
                      { label: 'Baru', value: '23', icon: <UserPlus className="w-5 h-5 text-emerald-500 dark:text-emerald-400" /> },
                      { label: 'Repeat', value: '312', icon: <RefreshCw className="w-5 h-5 text-blue-500 dark:text-blue-400" /> },
                      { label: 'Avg LTV', value: '450K', icon: <CreditCard className="w-5 h-5 text-amber-500 dark:text-amber-400" /> }
                    ].map((stat, idx) => (
                      <motion.div 
                        key={idx} 
                        className="bg-slate-50 dark:bg-zinc-900/80 rounded-lg p-2 text-center border border-slate-100 dark:border-zinc-800"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <div className="flex justify-center">{stat.icon}</div>
                        <div className="text-sm font-bold text-slate-900 dark:text-white">{stat.value}</div>
                        <div className="text-xs text-slate-500 dark:text-zinc-400">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                  {/* Customer Cards */}
                  <div className="space-y-3">
                    {[
                      { name: 'Budi Santoso', phone: '+62 812-xxxx-1234', orders: 12, spent: 'Rp 2.4jt', repeat: true },
                      { name: 'Ani Rahmawati', phone: '+62 813-xxxx-5678', orders: 5, spent: 'Rp 890K', repeat: true },
                      { name: 'Citra Dewi', phone: '+62 857-xxxx-9012', orders: 1, spent: 'Rp 250K', repeat: false }
                    ].map((customer, idx) => (
                      <motion.div 
                        key={idx} 
                        className="flex items-center gap-3 bg-slate-50 dark:bg-zinc-900/80 rounded-lg p-3 border border-slate-100 dark:border-zinc-800 hover:border-indigo-200 dark:hover:border-indigo-500/40 transition-colors"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + idx * 0.1 }}
                        whileHover={{ x: 4 }}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {customer.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900 dark:text-white truncate">{customer.name}</span>
                            {customer.repeat && <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 rounded">Repeat</span>}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-zinc-400">{customer.phone}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-slate-900 dark:text-white">{customer.spent}</div>
                          <div className="text-xs text-slate-500 dark:text-zinc-400">{customer.orders} pesanan</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </ShowcaseFrame>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Showcase: Product Catalog */}
        <section className="relative py-16 sm:py-24 px-4 bg-white dark:bg-zinc-900 overflow-hidden">
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <AnimatedSection direction="left" className="order-2 lg:order-1 w-full max-w-md mx-auto lg:max-w-none">
                <ShowcaseFrame testId="showcase-catalog-mockup">
                  {/* Stats */}
                  <div className="flex gap-6 mb-5 justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">24</div>
                      <div className="text-xs text-slate-500 dark:text-zinc-400">Produk</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400">22</div>
                      <div className="text-xs text-slate-500 dark:text-zinc-400">Aktif</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold tracking-tight text-slate-400 dark:text-zinc-500">2</div>
                      <div className="text-xs text-slate-500 dark:text-zinc-400">Nonaktif</div>
                    </div>
                  </div>
                  {/* Product Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: 'Kaos Polos Navy', price: 'Rp 89.000', stock: 45 },
                      { name: 'Hoodie Premium', price: 'Rp 199.000', stock: 12 },
                      { name: 'Celana Cargo', price: 'Rp 159.000', stock: 28 },
                      { name: 'Topi Snapback', price: 'Rp 75.000', stock: 67 }
                    ].map((product, idx) => (
                      <motion.div
                        key={idx}
                        className="rounded-xl p-3 border border-slate-200 bg-white hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-900/80 dark:hover:border-indigo-500/40 transition-colors"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.08 }}
                      >
                        <div className="w-full h-16 bg-slate-100 dark:bg-zinc-800 rounded-lg flex items-center justify-center mb-2">
                          <ShoppingCart className="w-7 h-7 text-slate-400 dark:text-zinc-500" />
                        </div>
                        <div className="text-xs font-medium text-slate-900 dark:text-white truncate">{product.name}</div>
                        <div className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{product.price}</div>
                        <div className="text-xs text-slate-500 dark:text-zinc-400">Stok: {product.stock}</div>
                      </motion.div>
                    ))}
                  </div>
                </ShowcaseFrame>
              </AnimatedSection>
              <AnimatedSection direction="right" className="order-1 lg:order-2">
                <SectionEyebrow className="mb-5">{t('features.payment')}</SectionEyebrow>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  {t('showcase.catalog.title')}
                </h2>
                <p className="text-base md:text-lg text-slate-600 dark:text-zinc-300 mb-7 max-w-lg">
                  {t('showcase.catalog.subtitle')}
                </p>
                <FeatureChecklist
                  testId="showcase-catalog-list"
                  direction="right"
                  items={[
                    t('showcase.catalog.feature1'),
                    t('showcase.catalog.feature2'),
                    t('showcase.catalog.feature3'),
                    t('showcase.catalog.feature4')
                  ]}
                />
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <PricingPlans onSelectPlan={handlePricingClick} currentPlan={user?.plan} />

        {/* Jalur Enterprise */}
        <section
          id="enterprise"
          data-testid="enterprise-section"
          className="relative py-16 sm:py-20 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden"
        >
          <SectionGlow position="right" />
          <div className="relative max-w-7xl mx-auto">
            <EnterpriseBand />
          </div>
        </section>

        {/* Keamanan, privasi, integrasi, dukungan — apa adanya */}
        <section
          id="keamanan"
          data-testid="trust-section-wrapper"
          className="relative py-20 sm:py-24 px-4 bg-white dark:bg-zinc-900 overflow-hidden"
        >
          <div className="relative max-w-7xl mx-auto">
            <SectionIntro
              testId="trust-heading"
              eyebrow={t('eyebrows.trust')}
              title={t('trustSection.title')}
              subtitle={t('trustSection.subtitle')}
              className="mb-12"
            />
            <TrustSection />
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="relative py-16 sm:py-24 px-4 bg-slate-50 dark:bg-[#0A0A0A] overflow-hidden">
          <SectionGlow position="right" />
          <div className="relative max-w-7xl mx-auto grid gap-10 lg:grid-cols-12 lg:gap-16">
            <AnimatedSection className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <SectionEyebrow className="mb-5">{t('eyebrows.faq')}</SectionEyebrow>
                <h2
                  className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {t('faq.title')}
                </h2>
                <p className="mt-4 text-base md:text-lg text-slate-600 dark:text-zinc-300">
                  {t('faq.subtitle')}
                </p>
                <p
                  data-testid="faq-note"
                  className="mt-7 border-l-2 border-indigo-500/40 pl-4 text-sm text-slate-500 dark:text-zinc-400"
                >
                  {t('faqNote')}
                </p>
                <a
                  href="mailto:support@nerava.id"
                  data-testid="faq-contact-link"
                  className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  support@nerava.id
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </AnimatedSection>

            <StaggerContainer className="lg:col-span-7 space-y-3" staggerDelay={0.06}>
              {faqItems.map((item, index) => (
                <StaggerItem key={index}>
                  <Accordion type="single" collapsible>
                    <AccordionItem 
                      value={`item-${index}`}
                      data-testid={`faq-item-${index + 1}`}
                      className="rounded-xl border border-slate-200 bg-white px-5 transition-colors hover:border-indigo-300 data-[state=open]:border-indigo-400 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-indigo-500/60 dark:data-[state=open]:border-indigo-500/70"
                    >
                      <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-slate-900 dark:text-white hover:no-underline">
                        <span className="flex items-start gap-3">
                          <span className="mt-0.5 font-mono text-xs text-indigo-500 dark:text-indigo-400">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          {item.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pl-9 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-16 sm:py-24 px-4 bg-white dark:bg-zinc-900 overflow-hidden">
          <div className="relative max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="relative overflow-hidden rounded-3xl border border-indigo-500/25 bg-zinc-950 px-6 py-14 sm:px-14 sm:py-16 text-center shadow-[0_40px_80px_-44px_rgba(79,70,229,0.6)]">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-indigo-500/25 blur-3xl"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/70 to-transparent"
                />

                <div className="relative">
                  <div className="mb-6 inline-flex items-center gap-2.5">
                    <span className="h-px w-8 bg-indigo-400" />
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">
                      {t('eyebrows.cta')}
                    </span>
                  </div>
                  <h2
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    {t('pricing.cta.title')}
                  </h2>
                  <p className="text-base md:text-lg text-zinc-300 mb-9 max-w-2xl mx-auto">
                    {t('pricing.cta.subtitle')}
                  </p>
                  <Link to="/register">
                    <Button
                      data-testid="final-cta-button"
                      className="group bg-indigo-600 text-white hover:bg-indigo-500 rounded-full px-8 py-6 text-base sm:text-lg font-semibold shadow-[0_18px_36px_-18px_rgba(79,70,229,0.9)] transition-transform hover:-translate-y-0.5"
                    >
                      {t('pricing.cta.button')}
                      <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <p className="mt-5 text-xs sm:text-sm text-zinc-500">
                    {t('pricing.cta.note')}
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-14 px-4 bg-zinc-950 text-zinc-400 border-t border-zinc-900 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8">
              <div className="col-span-2 md:col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4">
                  <img src="/images/logo.png" alt="Nerava" className="w-10 h-10 flex-shrink-0" />
                  <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
                </Link>
                <p className="text-sm">
                  {t('footer.description')}
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">{t('footer.product')}</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/ai-customer-service" data-testid="footer-acs-link" className="hover:text-indigo-400 transition-colors">AI Customer Service</Link></li>
                  <li><Link to="/ai-whatsapp" data-testid="footer-wa-link" className="hover:text-indigo-400 transition-colors">AI WhatsApp</Link></li>
                  <li><Link to="/crm-pesanan" data-testid="footer-crm-link" className="hover:text-indigo-400 transition-colors">CRM & Pesanan</Link></li>
                  <li><Link to="/harga" data-testid="footer-pricing-link" className="hover:text-indigo-400 transition-colors">{t('footer.pricing')}</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">{t('footer.company')}</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/tentang-kami" data-testid="footer-about-link" className="hover:text-indigo-400 transition-colors">Tentang Kami</Link></li>
                  <li><Link to="/blog" data-testid="footer-blog-link" className="hover:text-indigo-400 transition-colors">Blog</Link></li>
                  <li><a href="#enterprise" data-testid="footer-enterprise-link" className="hover:text-indigo-400 transition-colors">Enterprise</a></li>
                  <li><a href="#keamanan" className="hover:text-indigo-400 transition-colors">{t('eyebrows.trust')}</a></li>
                  <li>
                    <a href="mailto:sales@nerava.id" data-testid="footer-sales-link" className="hover:text-indigo-400 transition-colors">
                      sales@nerava.id
                    </a>
                  </li>
                </ul>
              </div>
              <div className="col-span-2 md:col-span-1">
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">{t('footer.contact')}</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a href="mailto:support@nerava.id" className="flex items-center gap-2 hover:text-indigo-400 transition-colors">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">support@nerava.id</span>
                    </a>
                  </li>
                  <li>
                    <a href="tel:+6285121288598" className="flex items-center gap-2 hover:text-indigo-400 transition-colors">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      +62 851-2128-8598
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-zinc-900 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
              <p className="text-sm">© 2026 Nerava. All rights reserved.</p>
              <div className="flex gap-4 sm:gap-6 text-sm">
                <Link to="/privacy-policy" className="hover:text-indigo-400 transition-colors">{t('footer.privacy')}</Link>
                <Link to="/terms" className="hover:text-indigo-400 transition-colors">{t('footer.terms')}</Link>
              </div>            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default LandingPage;
